import { DownloadOutlined, FileExcelOutlined, FileOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Dropdown, message, Modal, Space, Table, Typography } from 'antd';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { Outlet } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import { withRouteChange } from '../services/APM';
import { ReportCollection, ReportCollectionData } from '../types';

interface ReportColumn {
  type: ReportCollection['type'];
  title: string;
  subtitle: string;
  report?: ReportCollection;
}

const Reports = () => {
  const reports = useAPIQuery('/reports/');
  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    ({ type }: ReportCollectionData, api) => api.post('/reports/', { type }),
    {
      onSuccess: () => {
        message.success('O relatório está sendo gerado.');
      },
      onSettled: () => {
        queryClient.invalidateQueries('/reports/');
      },
    }
  );

  const columns: ReportColumn[] = [
    {
      title: 'Candidaturas nas vagas',
      subtitle: 'Informações das candidaturas recebidas e vagas relacionadas',
      type: 'APPLICATIONS',
      report: reports.data?.results.find((report) => report.type === 'APPLICATIONS'),
    },
    {
      title: 'Requisição de vagas',
      subtitle: 'Informações sobre a lista de requisições de vagas',
      type: 'REQUEST_JOB_POSTING',
      report: reports.data?.results.find((report) => report.type === 'REQUEST_JOB_POSTING'),
    },
  ];

  const showConfirmReportGenerationModal = (type: ReportCollection['type']) => {
    Modal.confirm({
      title: 'Gerar novo relatório',
      content: (
        <>
          <Typography.Text>
            Ao gerar um novo relatório a última extração será excluída.
          </Typography.Text>
          <br />
          <Typography.Text>Deseja prosseguir?</Typography.Text>
        </>
      ),
      okText: 'Sim',
      onOk: () => mutation.mutate({ type }),
      okButtonProps: {
        loading: mutation.isLoading,
      },
      cancelText: 'Não',
    });
  };

  return (
    <Page>
      <PageTitle title="Relatórios" />
      <ErrorResult error={reports.error}>
        <PageContent>
          <Outlet />
          <Card>
            <Space direction="vertical" size="middle">
              <Table<ReportColumn>
                dataSource={columns}
                loading={reports.isLoading || mutation.isLoading}
                rowKey="type"
                pagination={false}
              >
                <Table.Column<ReportColumn>
                  title="Tipo"
                  render={(value, record) => (
                    <>
                      <Typography.Paragraph strong>{record.title}</Typography.Paragraph>
                      <Typography.Text>{record.subtitle}</Typography.Text>
                    </>
                  )}
                />
                <Table.Column<ReportColumn>
                  title="Última extração"
                  render={(value, record) => (
                    <>
                      {record.report && (
                        <Typography.Text>
                          {record.report.status === 'IN_PROGRESS' && (
                            <Button icon={<FileOutlined />} disabled>
                              Carregando relatório
                            </Button>
                          )}
                          {record.report.status === 'SUCCESS' && (
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: 'csv',
                                    label: (
                                      <a href={record.report?.csv ?? ''}>
                                        <FileOutlined /> CSV (.csv)
                                      </a>
                                    ),
                                  },
                                  {
                                    key: 'xlsx',
                                    label: (
                                      <a href={record.report.xlsx ?? ''}>
                                        <FileExcelOutlined /> Microsoft Excel (.xlsx)
                                      </a>
                                    ),
                                  },
                                ],
                              }}
                            >
                              <Button icon={<DownloadOutlined />}>
                                Relatório de {moment(record.report?.updated_at).format('LLL')}{' '}
                              </Button>
                            </Dropdown>
                          )}
                          {record.report.status === 'FAILED' && (
                            <Alert
                              type="error"
                              description={
                                <Typography.Text>
                                  Erro ao gerar o relatório.
                                  <br />
                                  Contate o suporte.
                                </Typography.Text>
                              }
                              showIcon
                            />
                          )}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
                <Table.Column<ReportColumn>
                  title="Ações"
                  render={(value, record) => (
                    <Button
                      icon={<PlusOutlined />}
                      disabled={record.report?.status === 'IN_PROGRESS'}
                      onClick={() =>
                        record.report
                          ? showConfirmReportGenerationModal(record.type)
                          : mutation.mutate({ type: record.type })
                      }
                    >
                      Gerar novo relatório
                    </Button>
                  )}
                />
              </Table>
            </Space>
          </Card>
        </PageContent>
      </ErrorResult>
    </Page>
  );
};

export default withRouteChange()(Reports);
